'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useDropdown, type UseDropdownConfiguration } from '../../../hooks/helpers/useDropdown';
import { useOnClickOutside } from '../../../hooks/helpers/useOnClickOutside';
import { Divider } from '../../atoms/divider/Divider';
import { MenuItem, type MenuItemProps } from '../../atoms/menu-item/MenuItem';

export type MenuItemsProps = Array<Array<MenuItemProps>> | Array<MenuItemProps>;

export type MenuProps = {
	children: JSX.Element | string;
	disabled?: boolean;
	dropdownConfiguration?: UseDropdownConfiguration;
	items: MenuItemsProps;
	testID: string;
};

type Position = {
	x: number;
	y: number;
};

function isNestedArray(param: MenuItemsProps): param is Array<Array<MenuItemProps>> {
	return Array.isArray(param) && Array.isArray(param[0]);
}

export const Menu = ({ children, disabled = false, dropdownConfiguration, items, testID }: MenuProps) => {
	const dropdown = useRef<HTMLUListElement>(null);
	const activator = useRef<HTMLDivElement>(null);
	const { dropdownOpacity, dropdownPosition, hideDropdown, isDropdownVisible, openDropdown } = useDropdown(
		{
			activator,
			dropdown
		},
		dropdownConfiguration
	);
	const [dropdownMinWidth, setDropdownMinWidth] = useState<number>(0);

	useOnClickOutside(activator, hideDropdown);

	const onClick = useCallback(() => {
		if (!disabled) {
			openDropdown();
		}
	}, [disabled, openDropdown]);

	const renderedItems = useCallback(
		(itemsArray: Array<MenuItemProps>, segmentIndex: number) => {
			return itemsArray.map((item, index) => {
				return (
					<S.Item
						key={`${segmentIndex}-${index}-item`}
						{...item}
						onClick={() => {
							item?.onClick && item?.onClick();

							hideDropdown();
						}}
					/>
				);
			});
		},
		[hideDropdown]
	);

	useEffect(() => {
		setDropdownMinWidth(activator.current?.clientWidth || 0);
	}, [activator]);

	const menuItems = useMemo(
		() =>
			isNestedArray(items)
				? items.map((segment, segmentIndex) => (
						<>
							{segmentIndex > 0 && <Divider orientation="horizontal" />}
							{renderedItems(segment, segmentIndex)}
						</>
					))
				: renderedItems(items, 0),
		[items, renderedItems]
	);

	return (
		<S.Container $disabled={disabled} data-testid={testID}>
			<div ref={activator} onClick={onClick}>
				{children}
				<S.Dropdown
					$minWidth={dropdownMinWidth}
					$opacity={dropdownOpacity}
					$position={dropdownPosition}
					ref={dropdown}
					role="list"
					style={isDropdownVisible ? { display: 'block' } : { display: 'none' }}
				>
					{menuItems}
				</S.Dropdown>
			</div>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div<{ $disabled: boolean }>`
		position: relative;
		width: fit-content;
		${({ $disabled }) => $disabled && 'opacity: 0.3;'}
		cursor: pointer;
		${({ $disabled }) => ($disabled ? 'pointer-events: none;' : '')}
	`;

	export const Dropdown = styled.ul<{ $minWidth: number; $opacity: number; $position: Position }>`
		opacity: 0;
		position: absolute;
		overflow-y: auto;
		opacity: ${({ $opacity }) => $opacity};
		top: ${({ $position }) => $position.y}px;
		left: ${({ $position }) => $position.x}px;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.xsmall} 0;
		margin: 0;
		${({ theme }) => theme.border.radius.medium}
		${({ theme }) => theme.elevations.level1}
		z-index: 11;
		width: fit-content;
		min-width: ${({ $minWidth }) => $minWidth}px;
		max-height: 254px;
	`;

	export const Item = styled(MenuItem)`
		padding: ${({ theme }) => theme.spacings.xsmall} ${({ theme }) => theme.spacings.medium};
	`;
}
